import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Typography,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

export interface FilterAttribute {
  value: string;
  label: string;
  type: "string" | "number" | "date" | "enum";
  enumValues?: string[];
}

export interface Filter {
  id: string;
  attribute: string;
  operator: string;
  value: string;
  value2?: string;
}

interface FilterModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  attributes: FilterAttribute[];
  filters: Filter[];
  onFiltersChange: (filters: Filter[]) => void;
}

const FilterModal: React.FC<FilterModalProps> = ({
  open,
  onClose,
  title,
  attributes,
  filters,
  onFiltersChange,
}) => {
  const getOperatorsForType = (type: string): { value: string; label: string }[] => {
    switch (type) {
      case "string":
      case "enum":
        return [
          { value: "equals", label: "Equals" },
          { value: "contains", label: "Contains" },
        ];
      case "number":
        return [
          { value: "equals", label: "=" },
          { value: "greater", label: ">" },
          { value: "less", label: "<" },
          { value: "between", label: "Between" },
        ];
      case "date":
        return [
          { value: "equals", label: "On" },
          { value: "after", label: "After" },
          { value: "before", label: "Before" },
          { value: "between", label: "Between" },
        ];
      default:
        return [{ value: "equals", label: "=" }];
    }
  };

  const getAttributeType = (attributeValue: string): FilterAttribute | undefined => {
    return attributes.find((attr) => attr.value === attributeValue);
  };

  const addFilter = () => {
    const newFilter: Filter = {
      id: `filter-${Date.now()}`,
      attribute: attributes[0]?.value || "",
      operator: "equals",
      value: "",
    };
    onFiltersChange([...filters, newFilter]);
  };

  const removeFilter = (id: string) => {
    onFiltersChange(filters.filter((f) => f.id !== id));
  };

  const updateFilter = (id: string, field: keyof Filter, value: string) => {
    onFiltersChange(
      filters.map((f) => {
        if (f.id === id) {
          const updated = { ...f, [field]: value };
          if (field === "attribute") {
            const attr = getAttributeType(value);
            if (attr) {
              const operators = getOperatorsForType(attr.type);
              updated.operator = operators[0].value;
            }
          }
          if (field === "operator" && value !== "between") {
            delete updated.value2;
          }
          return updated;
        }
        return f;
      })
    );
  };

  const handleClear = () => {
    onFiltersChange([]);
  };

  const renderValueInput = (filter: Filter) => {
    const attribute = getAttributeType(filter.attribute);
    if (!attribute) return null;

    const isBetween = filter.operator === "between";

    // Enum type - use dropdown only for exact match; contains needs free text
    if (attribute.type === "enum" && attribute.enumValues && filter.operator === "equals") {
      return (
        <TextField
          select
          size="small"
          value={filter.value}
          onChange={(e) => updateFilter(filter.id, "value", e.target.value)}
          sx={{ 
            flex: 1,
            "& .MuiOutlinedInput-root": {
              color: "#f8fafc",
              "& fieldset": {
                borderColor: "rgba(148, 163, 184, 0.3)",
              },
              "&:hover fieldset": {
                borderColor: "rgba(148, 163, 184, 0.5)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#00d4ad",
              },
            },
            "& .MuiSelect-icon": {
              color: "#94a3b8",
            },
          }}
          SelectProps={{
            MenuProps: {
              sx: { zIndex: 99999999 }
            }
          }}
        >
          {attribute.enumValues.map((val) => (
            <MenuItem key={val} value={val}>
              {val}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    // Date type - use date input
    if (attribute.type === "date") {
      return (
        <Box sx={{ display: "flex", gap: 0.5, flex: 1 }}>
          <TextField
            type="date"
            size="small"
            value={filter.value}
            onChange={(e) => updateFilter(filter.id, "value", e.target.value)}
            sx={{ 
              flex: 1,
              "& .MuiOutlinedInput-root": {
                color: "#f8fafc",
                "& fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.3)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.5)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#00d4ad",
                },
              },
            }}
          />
          {isBetween && (
            <>
              <Typography sx={{ color: "#94a3b8", alignSelf: "center" }}>and</Typography>
              <TextField
                type="date"
                size="small"
                value={filter.value2 || ""}
                onChange={(e) => updateFilter(filter.id, "value2", e.target.value)}
                sx={{ 
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    color: "#f8fafc",
                    "& fieldset": {
                      borderColor: "rgba(148, 163, 184, 0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(148, 163, 184, 0.5)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#00d4ad",
                    },
                  },
                }}
              />
            </>
          )}
        </Box>
      );
    }

    // Number or String - use text input
    return (
      <Box sx={{ display: "flex", gap: 0.5, flex: 1 }}>
        <TextField
          type={attribute.type === "number" ? "number" : "text"}
          size="small"
          value={filter.value}
          onChange={(e) => updateFilter(filter.id, "value", e.target.value)}
          placeholder="Value"
          sx={{ 
            flex: 1,
            "& .MuiOutlinedInput-root": {
              color: "#f8fafc",
              "& fieldset": {
                borderColor: "rgba(148, 163, 184, 0.3)",
              },
              "&:hover fieldset": {
                borderColor: "rgba(148, 163, 184, 0.5)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#00d4ad",
              },
            },
            "& .MuiInputBase-input::placeholder": {
              color: "#64748b",
              opacity: 1,
            },
          }}
        />
        {isBetween && (
          <>
            <Typography sx={{ color: "#94a3b8", alignSelf: "center" }}>and</Typography>
            <TextField
              type={attribute.type === "number" ? "number" : "text"}
              size="small"
              value={filter.value2 || ""}
              onChange={(e) => updateFilter(filter.id, "value2", e.target.value)}
              placeholder="Value"
              sx={{ 
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  color: "#f8fafc",
                  "& fieldset": {
                    borderColor: "rgba(148, 163, 184, 0.3)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(148, 163, 184, 0.5)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#00d4ad",
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#64748b",
                  opacity: 1,
                },
              }}
            />
          </>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "rgba(30, 41, 59, 0.98)",
          backdropFilter: "blur(8px)",
          border: "1px solid rgba(148, 163, 184, 0.2)",
        },
      }}
    >
      <DialogTitle
        sx={{
          color: "#f8fafc",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid rgba(148, 163, 184, 0.2)",
        }}
      >
        <Box>
          <Typography variant="h6" component="span" sx={{ color: "#f8fafc" }}>
            {title} Filters
          </Typography>
          {filters.length > 0 && (
            <Chip
              label={`${filters.length} active`}
              size="small"
              sx={{
                ml: 1,
                backgroundColor: "#00d4ad",
                color: "#fff",
              }}
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#94a3b8" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {filters.map((filter) => {
            const attribute = getAttributeType(filter.attribute);
            const operators = attribute ? getOperatorsForType(attribute.type) : [];

            return (
              <Box
                key={filter.id}
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                  p: 2,
                  backgroundColor: "rgba(15, 23, 42, 0.5)",
                  borderRadius: 1,
                  border: "1px solid rgba(148, 163, 184, 0.1)",
                }}
              >
                <TextField
                  select
                  size="small"
                  label="Attribute"
                  value={filter.attribute}
                  onChange={(e) => updateFilter(filter.id, "attribute", e.target.value)}
                  sx={{ 
                    width: 200,
                    "& .MuiOutlinedInput-root": {
                      color: "#f8fafc",
                      "& fieldset": {
                        borderColor: "rgba(148, 163, 184, 0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(148, 163, 184, 0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#00d4ad",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#94a3b8",
                    },
                    "& .MuiSelect-icon": {
                      color: "#94a3b8",
                    },
                  }}
                  SelectProps={{
                    MenuProps: {
                      sx: { zIndex: 99999999 }
                    }
                  }}
                >
                  {attributes.map((attr) => (
                    <MenuItem key={attr.value} value={attr.value}>
                      {attr.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Operator"
                  value={filter.operator}
                  onChange={(e) => updateFilter(filter.id, "operator", e.target.value)}
                  sx={{ 
                    width: 140,
                    "& .MuiOutlinedInput-root": {
                      color: "#f8fafc",
                      "& fieldset": {
                        borderColor: "rgba(148, 163, 184, 0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(148, 163, 184, 0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#00d4ad",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#94a3b8",
                    },
                    "& .MuiSelect-icon": {
                      color: "#94a3b8",
                    },
                  }}
                  SelectProps={{
                    MenuProps: {
                      sx: { zIndex: 99999999 }
                    }
                  }}
                >
                  {operators.map((op) => (
                    <MenuItem key={op.value} value={op.value}>
                      {op.label}
                    </MenuItem>
                  ))}
                </TextField>

                {renderValueInput(filter)}

                <IconButton
                  size="small"
                  onClick={() => removeFilter(filter.id)}
                  sx={{ color: "#ef4444", mt: 0.5 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}

          {attributes.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                color: "#64748b",
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>No attributes available</Typography>
              <Typography variant="caption">
                This layer doesn't have filterable data. Filters only work with GeoJSON layers that have feature properties.
              </Typography>
            </Box>
          )}

          {attributes.length > 0 && filters.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                color: "#64748b",
              }}
            >
              <Typography variant="body2">No filters applied</Typography>
              <Typography variant="caption">Click "Add Filter" to create a new filter</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: "1px solid rgba(148, 163, 184, 0.2)",
          px: 3,
          py: 2,
          justifyContent: "space-between",
        }}
      >
        <Button
          startIcon={<AddIcon />}
          onClick={addFilter}
          disabled={attributes.length === 0}
          sx={{
            color: attributes.length === 0 ? "#64748b" : "#00d4ad",
            "&:hover": {
              backgroundColor: attributes.length === 0 ? "transparent" : "rgba(0, 212, 173, 0.1)",
            },
          }}
        >
          Add Filter
        </Button>
        <Button
          onClick={handleClear}
          sx={{
            color: "#94a3b8",
            "&:hover": {
              backgroundColor: "rgba(148, 163, 184, 0.1)",
            },
          }}
        >
          Clear All
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterModal;
